import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './CTALanding.css';

/**
 * Google Tag Manager & Ads Tracking Setup
 * =======================================
 * 
 * GTM is loaded from HTML head for optimal performance.
 * 
 * Tracking Events Configured:
 * - Lead Success: AW-17465397731/POpFCI6RsYUbEOOjk4hB (only Google Ads conversion; views/clicks are GA4 events)
 * 
 * UTM Parameters Tracked:
 * - utm_source, utm_medium, utm_campaign, utm_term, utm_content
 * - gclid (Google Ads), fbclid (Facebook Ads)
 */

function CTALanding() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Get UTM parameters and track page view
  useEffect(() => {
    // Wait a bit for GTM to fully load from HTML head, then track page view
    setTimeout(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const utmData = {
        utm_source: urlParams.get('utm_source'),
        utm_medium: urlParams.get('utm_medium'),
        utm_campaign: urlParams.get('utm_campaign'),
        utm_term: urlParams.get('utm_term'),
        utm_content: urlParams.get('utm_content'),
        gclid: urlParams.get('gclid'), // Google Ads click ID
        fbclid: urlParams.get('fbclid') // Facebook click ID
      };
      
      // Store UTM data for form submission
      window.utmData = utmData;
      
      // Track page view with GTM dataLayer
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'page_view',
        page_title: 'CTA Landing Page',
        page_location: window.location.href,
        utm_source: utmData.utm_source || 'direct',
        utm_medium: utmData.utm_medium || 'none',
        utm_campaign: utmData.utm_campaign || 'none'
      });

    }, 1000);

    // Track engagement - time on page and scroll depth
    let timeOnPage = 0;
    const timeInterval = setInterval(() => {
      timeOnPage += 10;
      // Track engagement every 30 seconds
      if (timeOnPage % 30 === 0 && typeof window.gtag !== 'undefined') {
        window.gtag('event', 'engagement_time', {
          event_category: 'engagement',
          event_label: 'time_on_page',
          value: timeOnPage
        });
      }
    }, 10000);

    // Track scroll depth
    let maxScroll = 0;
    const handleScroll = () => {
      const scrollPercent = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
      if (scrollPercent > maxScroll && scrollPercent % 25 === 0) {
        maxScroll = scrollPercent;
        if (typeof window.gtag !== 'undefined') {
          window.gtag('event', 'scroll', {
            event_category: 'engagement',
            event_label: 'scroll_depth',
            value: scrollPercent
          });
        }
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Cleanup function
    return () => {
      clearInterval(timeInterval);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Track form submission event with Google Ads conversion tracking
    if (typeof window.gtag !== 'undefined') {
      // Standard lead generation event
      window.gtag('event', 'generate_lead', {
        event_category: 'engagement',
        event_label: 'CTA Landing Form',
        value: 1,
        currency: 'USD',
        custom_parameters: {
          source: window.utmData?.utm_source || 'direct',
          medium: window.utmData?.utm_medium || 'none',
          campaign: window.utmData?.utm_campaign || 'none'
        }
      });

    }

    try {
      // Add UTM data to form submission
      const submissionData = {
        ...formData,
        ...window.utmData,
        source_page: 'cta-landing',
        timestamp: new Date().toISOString()
      };

      // Here you would typically send to your backend
      console.log('Form submission data:', submissionData);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Track Calendly button clicks
  const trackCalendlyClick = (location) => {
    if (typeof window.gtag !== 'undefined') {
      // Track button click
      window.gtag('event', 'click', {
        event_category: 'engagement',
        event_label: `Book Consultation - ${location}`,
        custom_parameters: {
          button_location: location,
          source: window.utmData?.utm_source || 'direct'
        }
      });

    }
  };

  const scrollToForm = () => {
    document.getElementById('contact-form').scrollIntoView({ 
      behavior: 'smooth',
      block: 'center'
    });
  };

  if (submitted) {
    // Track successful form submission on success page
    React.useEffect(() => {
      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17465397731/POpFCI6RsYUbEOOjk4hB',
          'value': 1.0,
          'currency': 'USD'
        });
      }
    }, []);

    return (
      <div className="cta-landing">
        <div className="success-container">
          <div className="success-content">
            <div className="success-icon">✓</div>
            <h1>Thank You!</h1>
            <p>Your request has been submitted successfully. Our team will contact you within 24 hours.</p>
            <div className="success-next-steps">
              <h3>What happens next?</h3>
              <ul>
                <li>We'll review your business requirements</li>
                <li>Schedule a personalized consultation</li>
                <li>Provide a custom solution proposal</li>
              </ul>
            </div>
            <Link to="/" className="btn-secondary">Return to Homepage</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cta-landing">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1>The Future of Bookkeeping, RKube</h1>
            <p className="hero-subtitle">
              Remote, AI-powered bookkeeping at a fraction of the cost — risk-free trial included
            </p>
            <a 
              href="https://calendly.com/rohit-r-kumar/30min" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-consultation"
              onClick={() => trackCalendlyClick('hero')}
            >
              <span className="consultation-icon">📅</span>
              Book My Consultation
            </a>
          </div>
        </div>
      </section>

      {/* Our Clients Section */}
      <section className="our-clients-section">
        <div className="container">
          <h2>Our Clients</h2>
          <div className="clients-grid">
            <div className="client-card">
              <div className="client-icon">💰</div>
              <h3>Freelancers & Professionals</h3>
              <p>Focus on your work — we'll handle the numbers. Affordable, stress-free bookkeeping that keeps you compliant and ready for tax season.</p>
            </div>
            <div className="client-card">
              <div className="client-icon">💼</div>
              <h3>Start-Ups and Businesses</h3>
              <p>Keep your books accurate and tax-ready without the cost of an in-house team. We handle everything remotely and give you AI-powered monthly dashboards so you can make smart business decisions.</p>
            </div>
            <div className="client-card">
              <div className="client-icon">📊</div>
              <h3>CPAs & Accounting Firms</h3>
              <p>White-label bookkeeping services you can trust. Dedicated offshore teams, 24-hour turnaround, and AI insights so you can serve more clients without adding staff.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-benefits-section">
        <div className="container">
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">🌐</div>
              <h3>100% Remote & Online</h3>
              <p>Work with us from anywhere – we handle everything securely in the cloud with VMs.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">💎</div>
              <h3>High Quality – Low Rates</h3>
              <p>Premium bookkeeping at offshore rates, handled by trained degree-holding professionals.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">🤖</div>
              <h3>AI Monthly Dashboards</h3>
              <p>Personalized insights that actually help you run your business better.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">👥</div>
              <h3>Dedicated Team for Every Client</h3>
              <p>Same trusted team works with you every month.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">💰</div>
              <h3>Unbeatable Pricing</h3>
              <p>Save up to 70% compared to hiring in-house staff.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">🎯</div>
              <h3>Risk-Free Offer</h3>
              <p>Try us for free – stay only if you love the results.</p>
            </div>
          </div>
        </div>
      </section>



      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="container">
          <h2>What Our Clients Say</h2>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-content">
                <div className="stars">⭐⭐⭐⭐⭐</div>
                <p>"Saved us 20+ hours per month on bookkeeping. The AI categorization is incredibly accurate."</p>
              </div>
              <div className="testimonial-author">
                <img src="/images/testimonials/i-1.png" alt="Ryan Wenger" />
                <div>
                  <h4>Ryan Wenger</h4>
                  <p>Co-Founder, Inhouse</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-content">
                <div className="stars">⭐⭐⭐⭐⭐</div>
                <p>"Best decision we made for our startup. Professional service at a fraction of traditional firm costs."</p>
              </div>
              <div className="testimonial-author">
                <img src="/images/testimonials/i-2.png" alt="Clayton Jacobs" />
                <div>
                  <h4>Clayton Jacobs</h4>
                  <p>Founder & CEO, CreatorDB</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-content">
                <div className="stars">⭐⭐⭐⭐⭐</div>
                <p>"The compliance automation has been a game-changer. We never worry about missing deadlines anymore."</p>
              </div>
              <div className="testimonial-author">
                <img src="/images/testimonials/i-3.png" alt="Nikolay Bonev" />
                <div>
                  <h4>Nikolay Bonev</h4>
                  <p>Co-Founder & CTO, Shelf</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Contact Form */}
      <section id="contact-form" className="cta-landing-contact-form-section" style={{ display: 'none' }}>
        <div className="container">
          <div className="form-container">
            <div className="form-header">
              <h2>Get Your Free Consultation</h2>
              <p>Schedule a 15-minute call to discuss your business needs and get a custom quote</p>
            </div>
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Business Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="company">Company Name *</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <button 
                type="submit" 
                className="btn-primary large"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Get Free Consultation'}
              </button>
              <p className="form-disclaimer">
                By submitting this form, you agree to receive communications from us. 
                We respect your privacy and will never share your information.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="container">
          <h2>Ready to Transform Your Bookkeeping?</h2>
          <p>Book a consultation today and discover the future of accounting</p>
          <a 
            href="https://calendly.com/rohit-r-kumar/30min" 
            target="_blank" 
            rel="noopener noreferrer"
            className="btn-consultation large"
            onClick={() => trackCalendlyClick('final_cta')}
          >
            <span className="consultation-icon">📅</span>
            Book My Consultation
          </a>
        </div>
      </section>
    </div>
  );
}

export default CTALanding;
